import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import api from '../services/api';
import { showToast } from '../components/ToastNotification';
import './UserProfile.css';

const ROLE_LABELS = {
  admin: 'Administrátor',
  manager: 'Manažer',
  editor: 'Editor',
  viewer: 'Uživatel',
};

const MIN_PASSWORD_LENGTH = 6;

function UserProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Osobní údaje
  const [names, setNames] = useState({ first_name: '', last_name: '' });
  const [savingNames, setSavingNames] = useState(false);
  const [namesError, setNamesError] = useState('');

  // Změna hesla
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdError, setPwdError] = useState('');

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const result = await api.getProfile();
      const data = result.data || {};
      setProfile(data);
      setNames({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
      });
    } catch (err) {
      console.error('Failed to load profile:', err);
      setLoadError(err.message || 'Načtení profilu se nepodařilo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveNames = async (e) => {
    e.preventDefault();
    setNamesError('');
    setSavingNames(true);
    try {
      const result = await api.updateProfile({
        first_name: names.first_name.trim(),
        last_name: names.last_name.trim(),
      });
      const updated = result.data || {};
      setProfile(updated);
      setNames({
        first_name: updated.first_name || '',
        last_name: updated.last_name || '',
      });
      // Jméno je i v hlavičce vedle avataru — App.js na tuhle událost
      // přepíše currentUser, ať se změna projeví bez reloadu.
      window.dispatchEvent(new CustomEvent('user:updated', { detail: updated }));
      showToast('Údaje byly uloženy.');
    } catch (err) {
      console.error('Failed to update profile:', err);
      setNamesError(err.message || 'Uložení se nepodařilo.');
    } finally {
      setSavingNames(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdError('');

    if (!pwd.current || !pwd.next) {
      setPwdError('Vyplň stávající i nové heslo.');
      return;
    }
    if (pwd.next.length < MIN_PASSWORD_LENGTH) {
      setPwdError(`Nové heslo musí mít alespoň ${MIN_PASSWORD_LENGTH} znaků.`);
      return;
    }
    if (pwd.next !== pwd.confirm) {
      setPwdError('Nové heslo a jeho potvrzení se neshodují.');
      return;
    }

    setSavingPwd(true);
    try {
      await api.changePassword(pwd.current, pwd.next);
      setPwd({ current: '', next: '', confirm: '' });
      showToast('Heslo bylo změněno.');
    } catch (err) {
      console.error('Failed to change password:', err);
      setPwdError(err.message || 'Změna hesla se nepodařila.');
    } finally {
      setSavingPwd(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="px-4 py-4 text-center text-muted">
        <Spinner animation="border" size="sm" className="me-2" />
        Načítám profil…
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container fluid className="px-4 py-3">
        <Alert variant="danger">{loadError}</Alert>
        <Button variant="outline-secondary" size="sm" onClick={loadProfile}>
          <i className="bi bi-arrow-clockwise me-1"></i>Zkusit znovu
        </Button>
      </Container>
    );
  }

  const fullName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim();

  return (
    <Container fluid className="user-profile px-4 py-3">
      <div className="up-header mb-4">
        <i className="bi bi-person-circle up-avatar" aria-hidden="true"></i>
        <div>
          <h4 className="mb-1">{fullName || profile?.email}</h4>
          <div className="text-muted small">
            {profile?.email}
            <span className="mx-2">·</span>
            {ROLE_LABELS[profile?.role] || profile?.role}
          </div>
        </div>
      </div>

      <Row className="g-4">
        <Col lg={6}>
          <div className="card h-100">
            <div className="card-header bg-white fw-semibold">Osobní údaje</div>
            <div className="card-body">
              {namesError && (
                <Alert variant="danger" onClose={() => setNamesError('')} dismissible>
                  {namesError}
                </Alert>
              )}

              <Form onSubmit={handleSaveNames}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Jméno</Form.Label>
                      <Form.Control
                        type="text"
                        value={names.first_name}
                        onChange={(e) => setNames((p) => ({ ...p, first_name: e.target.value }))}
                        autoComplete="given-name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Příjmení</Form.Label>
                      <Form.Control
                        type="text"
                        value={names.last_name}
                        onChange={(e) => setNames((p) => ({ ...p, last_name: e.target.value }))}
                        autoComplete="family-name"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Login (email)</Form.Label>
                  <Form.Control type="text" value={profile?.email || ''} disabled readOnly />
                </Form.Group>

                <div className="d-flex justify-content-end">
                  <Button variant="primary" type="submit" disabled={savingNames}>
                    {savingNames ? 'Ukládám…' : 'Uložit změny'}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </Col>

        <Col lg={6}>
          <div className="card h-100">
            <div className="card-header bg-white fw-semibold">Změna hesla</div>
            <div className="card-body">
              {pwdError && (
                <Alert variant="danger" onClose={() => setPwdError('')} dismissible>
                  {pwdError}
                </Alert>
              )}

              <Form onSubmit={handleChangePassword}>
                <Form.Group className="mb-3">
                  <Form.Label>Stávající heslo</Form.Label>
                  <Form.Control
                    type="password"
                    value={pwd.current}
                    onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
                    autoComplete="current-password"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Nové heslo</Form.Label>
                  <Form.Control
                    type="password"
                    value={pwd.next}
                    onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
                    autoComplete="new-password"
                  />
                  <Form.Text className="text-muted">
                    Minimálně {MIN_PASSWORD_LENGTH} znaků.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Nové heslo znovu</Form.Label>
                  <Form.Control
                    type="password"
                    value={pwd.confirm}
                    onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                    autoComplete="new-password"
                  />
                </Form.Group>

                <div className="d-flex justify-content-end">
                  <Button variant="primary" type="submit" disabled={savingPwd}>
                    {savingPwd ? 'Měním…' : 'Změnit heslo'}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default UserProfile;
